import type { PotentialExposuresResponseData, SuppressionRecord } from '@/types/person/vault';
import { exposuresService } from '~/services/exposures-service';
import { usePersonStore } from '~/stores/person';
import { useContactsStore } from '~/stores/person/contacts';
import { formatExposureAddress, isPartialAddressMatch } from '~/utils/address-matching';
import { arePhoneNumbersEqual } from '~/utils/phone-matching';

export const useVaultStore = defineStore('vault', () => {
    const rawPotentialExposures = ref<PotentialExposuresResponseData>();
    const suppressedRecords = ref<SuppressionRecord[]>([]);
    const personStore = usePersonStore();
    const { showSuccess, showError } = useGlobalToast();

    // Cache expiration time (1 hour)
    const CACHE_EXPIRY = 60 * 60 * 1000;
    const potentialExposuresCacheTimestamp = ref<number>(0);
    
    // Loading state to prevent multiple simultaneous calls
    const isLoadingPotentialExposures = ref<boolean>(false);
    const hasRecentError = ref<boolean>(false);
    let pendingFetchPromise: Promise<void> | null = null;
    let errorTimeout: NodeJS.Timeout | null = null;

    // Helper function to check if an exposure phone matches vault phones
    const isPhoneInVault = (exposurePhone: string): boolean => {
        if (!personStore.person?.phone_numbers) return false;
        
        return personStore.person.phone_numbers.some(vaultPhone => 
            arePhoneNumbersEqual(exposurePhone, vaultPhone.phone_full)
        );
    };

    // Helper function to check if an exposure address matches vault addresses
    const isAddressInVault = (exposureAddressObj: any): boolean => {
        if (!personStore.person?.addresses) return false;
        
        const personAddresses = personStore.person.addresses;
        return personAddresses.some(vaultAddr => 
            isPartialAddressMatch(exposureAddressObj, {
                ...vaultAddr,
                region: vaultAddr.region || undefined
            })
        );
    };

    // Helper function to calculate age from date of birth
    const calculateAge = (dateOfBirth: string): number | null => {
        if (!dateOfBirth) return null;
        
        const birthDate = new Date(dateOfBirth);
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            age--;
        }
        
        return age;
    };

    // Helper function to normalize whitespace in strings
    const normalizeString = (str: string): string => {
        return str.replace(/\s+/g, ' ').trim();
    };

    // Helper function to capitalize names for display
    const capitalizeNameForDisplay = (name: string): string => {
        if (!name) return '';
        return name.split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
    };

    // Helper function to check if one address is less specific than another
    const isLessSpecificAddress = (address1: any, address2: any): boolean => {
        const addr1Formatted = formatExposureAddress(address1.value);
        const addr2Formatted = formatExposureAddress(address2.value);
        
        // If addresses are the same after normalization, neither is less specific
        if (addr1Formatted === addr2Formatted) return false;
        
        // Helper to normalize address components for comparison
        const normalizeComponent = (component: string, type: 'country' | 'state' | 'other' = 'other'): string => {
            if (!component) return '';
            
            if (type === 'country') {
                // Use our country normalization logic
                const normalized = component.toLowerCase().trim();
                if (['us', 'usa', 'united states', 'united states of america', 'america'].includes(normalized)) {
                    return 'united states';
                }
                return normalized;
            }
            
            if (type === 'state') {
                // Normalize US states to abbreviations for comparison
                const normalized = component.toLowerCase().trim();
                const stateAbbreviations: Record<string, string> = {
                    'alabama': 'al', 'alaska': 'ak', 'arizona': 'az', 'arkansas': 'ar', 'california': 'ca',
                    'colorado': 'co', 'connecticut': 'ct', 'delaware': 'de', 'florida': 'fl', 'georgia': 'ga',
                    'hawaii': 'hi', 'idaho': 'id', 'illinois': 'il', 'indiana': 'in', 'iowa': 'ia',
                    'kansas': 'ks', 'kentucky': 'ky', 'louisiana': 'la', 'maine': 'me', 'maryland': 'md',
                    'massachusetts': 'ma', 'michigan': 'mi', 'minnesota': 'mn', 'mississippi': 'ms', 'missouri': 'mo',
                    'montana': 'mt', 'nebraska': 'ne', 'nevada': 'nv', 'new hampshire': 'nh', 'new jersey': 'nj',
                    'new mexico': 'nm', 'new york': 'ny', 'north carolina': 'nc', 'north dakota': 'nd', 'ohio': 'oh',
                    'oklahoma': 'ok', 'oregon': 'or', 'pennsylvania': 'pa', 'rhode island': 'ri', 'south carolina': 'sc',
                    'south dakota': 'sd', 'tennessee': 'tn', 'texas': 'tx', 'utah': 'ut', 'vermont': 'vt',
                    'virginia': 'va', 'washington': 'wa', 'west virginia': 'wv', 'wisconsin': 'wi', 'wyoming': 'wy'
                };
                
                // If it's already an abbreviation (2 characters), return as is
                if (normalized.length === 2) {
                    return normalized;
                }
                
                // If it's a full state name, convert to abbreviation
                return stateAbbreviations[normalized] || normalized;
            }
            
            return component.toLowerCase().trim();
        };
        
        // Get normalized components for comparison
        const getNormalizedComponents = (addr: any) => {
            return [
                normalizeComponent(addr.line1, 'other'),
                normalizeComponent(addr.line2, 'other'),
                normalizeComponent(addr.city, 'other'),
                normalizeComponent(addr.state || addr.region, 'state'),
                normalizeComponent(addr.postal_code, 'other'),
                normalizeComponent(addr.country, 'country')
            ].filter(component => component && component !== '');
        };
        
        const addr1Parts = getNormalizedComponents(address1.value);
        const addr2Parts = getNormalizedComponents(address2.value);
        

        
        // If address1 has fewer components, check if it's contained within address2
        if (addr1Parts.length < addr2Parts.length) {
            // Check if all parts of address1 are contained in address2
            return addr1Parts.every(part => addr2Parts.includes(part));
        }
        
        return false;
    };

    // Helper function to deduplicate addresses, keeping only the most specific ones
    const deduplicateAddresses = (addresses: any[]): any[] => {
        const filtered: any[] = [];
        
        for (const address of addresses) {
            // First check if this address is an exact match of an existing address
            const exactMatch = filtered.find(existingAddr => {
                const addr1Formatted = formatExposureAddress(address.value);
                const addr2Formatted = formatExposureAddress(existingAddr.value);
                return addr1Formatted === addr2Formatted;
            });
            
            if (exactMatch) {
                // Merge companies from the duplicate address into the existing one
                const allCompanies = [...exactMatch.companies, ...address.companies];
                // Remove duplicate companies based on company_id
                exactMatch.companies = allCompanies.filter((company, index, self) =>
                    index === self.findIndex(c => c.company_id === company.company_id)
                );
                continue; // Skip to next address since this one was merged
            }
            
            // Check if this address is less specific than any address already in the filtered list
            const moreSpecificExisting = filtered.find(existingAddr => 
                isLessSpecificAddress(address, existingAddr)
            );
            
            if (moreSpecificExisting) {
                // Merge companies from the less specific address into the more specific one
                const allCompanies = [...moreSpecificExisting.companies, ...address.companies];
                // Remove duplicate companies based on company_id
                moreSpecificExisting.companies = allCompanies.filter((company, index, self) =>
                    index === self.findIndex(c => c.company_id === company.company_id)
                );
            } else {
                // Check if there are any existing addresses that are less specific than this one
                const lessSpecificAddresses = filtered.filter(existingAddr => 
                    isLessSpecificAddress(existingAddr, address)
                );
                
                if (lessSpecificAddresses.length > 0) {
                    // Remove the less specific addresses and merge their companies into this one
                    const moreSpecificFiltered = filtered.filter(existingAddr => 
                        !isLessSpecificAddress(existingAddr, address)
                    );
                    
                    // Merge companies from all less specific addresses
                    let allCompanies = [...address.companies];
                    lessSpecificAddresses.forEach(lessSpecific => {
                        allCompanies = [...allCompanies, ...lessSpecific.companies];
                    });
                    
                    // Remove duplicate companies based on company_id
                    address.companies = allCompanies.filter((company, index, self) =>
                        index === self.findIndex(c => c.company_id === company.company_id)
                    );
                    
                    moreSpecificFiltered.push(address);
                    filtered.splice(0, filtered.length, ...moreSpecificFiltered);
                } else {
                    // No conflicts, add the address as is
                    filtered.push(address);
                }
            }
        }
        
        return filtered;
    };

    // Helper function to check if an item is suppressed (fixed object/string handling)
    const isItemSuppressed = (type: string, value: any): boolean => {
        return suppressedRecords.value.some(record => {
            // Handle different type mappings between exposures and suppressions
            let matchesType = false;
            
            if (type === 'name' && (record.type === 'name' || record.type === 'aka')) {
                matchesType = true;
            } else if (type === 'aka' && (record.type === 'name' || record.type === 'aka')) {
                matchesType = true;
            } else if (record.type === type) {
                matchesType = true;
            }
            
            if (!matchesType) return false;
            
            // Handle different data formats with normalized whitespace
            let matchesData = false;
            
            if (type === 'name' || type === 'aka') {
                const valueString = String(value);
                let recordString = '';
                
                // Handle both string and object formats in suppression data
                if (typeof record.data === 'string') {
                    recordString = record.data;
                } else if (typeof record.data === 'object' && record.data !== null) {
                    // Convert object to string format like "First Middle Last"
                    const parts = [
                        (record.data as any).first_name || '',
                        (record.data as any).middle_name || '',
                        (record.data as any).last_name || ''
                    ].filter(Boolean);
                    recordString = parts.join(' ');
                }
                
                matchesData = normalizeString(valueString) === normalizeString(recordString);
            } else if (type === 'address') {
                // Special handling for addresses - compare formatted strings
                let addressString = '';
                if (typeof record.data === 'string') {
                    addressString = record.data;
                } else if (typeof record.data === 'object' && record.data !== null) {
                    // Format the suppressed address object the same way as exposures
                    addressString = formatExposureAddress(record.data as any);
                }
                
                matchesData = addressString === String(value);
            } else if (typeof record.data === 'string') {
                matchesData = record.data === String(value);
            } else if (type === 'relative') {
                const relativeData = record.data as any;
                matchesData = relativeData.first_name === value.first_name && relativeData.last_name === value.last_name && relativeData.age === value.age;
            } else {
                matchesData = JSON.stringify(record.data) === JSON.stringify(value);
            }
            
            return matchesData && record.me === 0; // 0 means suppressed
        });
    };

    // Helper function to check if an item is confirmed (fixed object/string handling)
    const isItemConfirmed = (type: string, value: any): boolean => {
        return suppressedRecords.value.some(record => {
            // Handle different type mappings between exposures and suppressions
            let matchesType = false;
            
            if (type === 'name' && (record.type === 'name' || record.type === 'aka')) {
                matchesType = true;
            } else if (type === 'aka' && (record.type === 'name' || record.type === 'aka')) {
                matchesType = true;
            } else if (record.type === type) {
                matchesType = true;
            }
            
            if (!matchesType) return false;
            
            // Handle different data formats with normalized whitespace
            let matchesData = false;
            
            if (type === 'name' || type === 'aka') {
                const valueString = String(value);
                let recordString = '';
                
                // Handle both string and object formats in suppression data
                if (typeof record.data === 'string') {
                    recordString = record.data;
                } else if (typeof record.data === 'object' && record.data !== null) {
                    // Convert object to string format like "First Middle Last"
                    const parts = [
                        (record.data as any).first_name || '',
                        (record.data as any).middle_name || '',
                        (record.data as any).last_name || ''
                    ].filter(Boolean);
                    recordString = parts.join(' ');
                }
                
                matchesData = normalizeString(valueString) === normalizeString(recordString);
            } else if (type === 'address') {
                // Special handling for addresses - compare formatted strings (same as suppression logic)
                let addressString = '';
                if (typeof record.data === 'string') {
                    addressString = record.data;
                } else if (typeof record.data === 'object' && record.data !== null) {
                    addressString = formatExposureAddress(record.data as any);
                }
               
                matchesData = addressString === String(value);
            } else if (typeof record.data === 'string') {
                matchesData = record.data === String(value);
            } else {
                matchesData = JSON.stringify(record.data) === JSON.stringify(value);
            }
            
            return matchesData && record.me === 1; // 1 means confirmed
        });
    };

    // Computed property to get processed potential exposures
    const potentialExposures = computed(() => {
        if (!rawPotentialExposures.value) {
            return undefined;
        }

        const processed: PotentialExposuresResponseData = {
            name: [],
            age: [],
            addresses: [],
            relatives: [],
            aka: [],
            emails: [],
            phones: [],
            date_of_birth: []
        };

        // Calculate person's current age from DOB
        const personAge = personStore.person?.dob ? calculateAge(personStore.person.dob) : null;

        // Process each exposure type
        Object.entries(rawPotentialExposures.value).forEach(([type, exposures]) => {
            if (!Array.isArray(exposures)) return;

            exposures.forEach(exposure => {
                let shouldInclude = true;
                let confirmed = false;
                let suppressed = false;

                // First check if this exposure is masked - if so, exclude it from potential exposures
                // since it will be shown in the Partial Records section instead
                let isMasked = false;
                if (type === 'emails' || type === 'phones' || type === 'name' || type === 'aka') {
                    isMasked = isMaskedValue(exposure.value, type);
                } else if (type === 'addresses') {
                    const addressParts = [
                        exposure.value.line1,
                        exposure.value.line2,
                        exposure.value.city,
                        exposure.value.state,
                        exposure.value.postal_code,
                        exposure.value.country
                    ];
                    isMasked = addressParts.some(part => isMaskedValue(part, type));
                } else if (type === 'relatives') {
                    const relativeParts = [
                        exposure.value.first_name,
                        exposure.value.last_name,
                        exposure.value.middle_name
                    ];
                    isMasked = relativeParts.some(part => isMaskedValue(part, type));
                } else {
                    isMasked = isMaskedValue(exposure.value, type);
                }

                // Skip masked exposures - they'll be shown in Partial Records instead
                if (isMasked) {
                    return;
                }

                // Check if item is suppressed or confirmed
                if (type === 'addresses') {
                    const addressString = formatExposureAddress(exposure.value);
                    confirmed = isItemConfirmed('address', addressString);
                    suppressed = isItemSuppressed('address', addressString);
                    if (!confirmed && isAddressInVault(exposure.value)) {
                        shouldInclude = false;
                    }
                } else if (type === 'emails') {
                    confirmed = isItemConfirmed('email', exposure.value);
                    suppressed = isItemSuppressed('email', exposure.value);
                    
                    if (!confirmed && personStore.person?.emails?.some(email => email.email === exposure.value)) {
                        shouldInclude = false;
                    }
                } else if (type === 'phones') {
                    confirmed = isItemConfirmed('phone', exposure.value);
                    suppressed = isItemSuppressed('phone', exposure.value);
                    
                    if (!confirmed && isPhoneInVault(exposure.value)) {
                        shouldInclude = false;
                    }
                } else if (type === 'age') {
                    confirmed = isItemConfirmed('age', exposure.value);
                    suppressed = isItemSuppressed('age', exposure.value);
                    
                    if (!confirmed && personAge !== null && parseInt(String(exposure.value)) === personAge) {
                        shouldInclude = false;
                    }
                } else if (type === 'date_of_birth') {
                    confirmed = isItemConfirmed('date_of_birth', exposure.value);
                    suppressed = isItemSuppressed('date_of_birth', exposure.value);
                    
                    if (!confirmed && personStore.person?.dob && 
                        new Date(exposure.value).toDateString() === new Date(personStore.person.dob).toDateString()) {
                        shouldInclude = false;
                    }
                } else if (type === 'name') {
                    // Names are now strings, not objects
                    const nameString = String(exposure.value);
                    confirmed = isItemConfirmed('name', nameString);
                    suppressed = isItemSuppressed('name', nameString);
                    
                    if (!confirmed && personStore.person?.name && normalizeString(personStore.person.name) === normalizeString(nameString)) {
                        shouldInclude = false;
                    }
                } else if (type === 'aka') {
                    confirmed = isItemConfirmed('aka', exposure.value);
                    suppressed = isItemSuppressed('aka', exposure.value);
                } else if (type === 'relatives') {
                   
                    confirmed = isItemConfirmed('relative', exposure.value);
                    suppressed = isItemSuppressed('relative', exposure.value);

                    // Check if relative is already in contacts
                    if (!confirmed) {
                        const contactsStore = useContactsStore();
                        const relativeName = `${exposure.value.first_name} ${exposure.value.last_name}`.trim();
                        const existingContact = contactsStore.contacts?.find(contact => 
                            normalizeString(contact.name) === normalizeString(relativeName)
                        );
                        if (existingContact) {
                            shouldInclude = false;
                        }
                    }
                } else {
                    // Handle other types
                    const suppressionType = type;
                    confirmed = isItemConfirmed(suppressionType, exposure.value);
                    suppressed = isItemSuppressed(suppressionType, exposure.value);
                }

                // Filter out suppressed items (unless they're confirmed)
                if (suppressed && !confirmed) {
                    shouldInclude = false;
                }

                if (shouldInclude) {
                    processed[type as keyof PotentialExposuresResponseData].push({
                        ...exposure,
                        confirmed,
                        suppressed
                    });
                }
            });
        });

        // Apply deduplication to addresses to remove less specific ones
        if (processed.addresses && processed.addresses.length > 0) {
            processed.addresses = deduplicateAddresses(processed.addresses);
        }

        return processed;
    });

    // Computed property to check if there are any potential exposures
    const hasExposures = computed(() => {
    });

    // Computed property to check if there are any unsuppressed and unconfirmed exposures
    const hasUnsuppressedUnconfirmedExposures = computed(() => {
        if (!potentialExposures.value) {
            return false;
        }
        
        return Object.values(potentialExposures.value).some((arr) => {
            if (!Array.isArray(arr)) return false;
            
            return arr.some(item => 
                !item.suppressed && !item.confirmed
            );
        });
    });

    // Helper function to check if a value contains masking characters
    const isMaskedValue = (value: any, type: string): boolean => {
        if (!value) return false;
        
        const stringValue = String(value);
        
        // Check for common masking patterns
        const maskingPatterns = [
            /\*{2,}/, // Two or more asterisks
            /x{2,}/i, // Two or more x's (case insensitive)
            /#{2,}/, // Two or more hash symbols
            /-{3,}/, // Three or more dashes
            /_{3,}/, // Three or more underscores
        ];
        
        return maskingPatterns.some(pattern => pattern.test(stringValue));
    };

    // Computed property to get partial/masked exposures
    const partialExposures = computed(() => {
        if (!rawPotentialExposures.value) {
            return [];
        }

        const masked: any[] = [];

        // Check each exposure type for masked values
        Object.entries(rawPotentialExposures.value).forEach(([type, exposures]) => {
            if (!Array.isArray(exposures)) return;

            exposures.forEach(exposure => {
                let isMasked = false;
                let displayValue = '';
                
                if (type === 'emails') {
                    isMasked = isMaskedValue(exposure.value, type);
                    displayValue = exposure.value;
                } else if (type === 'phones') {
                    isMasked = isMaskedValue(exposure.value, type);
                    displayValue = exposure.value;
                } else if (type === 'addresses') {
                    // Check if any part of the address is masked
                    const addressParts = [
                        exposure.value.line1,
                        exposure.value.line2,
                        exposure.value.city,
                        exposure.value.state,
                        exposure.value.postal_code,
                        exposure.value.country
                    ];
                    
                    isMasked = addressParts.some(part => isMaskedValue(part, type));
                    displayValue = formatExposureAddress(exposure.value);
                } else if (type === 'name' || type === 'aka') {
                    isMasked = isMaskedValue(exposure.value, type);
                    displayValue = exposure.value;
                } else if (type === 'relatives') {
                    // Check if any part of the relative data is masked
                    const relativeParts = [
                        exposure.value.first_name,
                        exposure.value.last_name,
                        exposure.value.middle_name
                    ];
                    
                    isMasked = relativeParts.some(part => isMaskedValue(part, type));
                    displayValue = `${exposure.value.first_name || ''} ${exposure.value.last_name || ''}`.trim() + 
                                  (exposure.value.age ? `, ${exposure.value.age} years old` : '');
                } else {
                    // For other types like age, date_of_birth
                    isMasked = isMaskedValue(exposure.value, type);
                    displayValue = String(exposure.value);
                }

                if (isMasked) {
                    // Map plural types to singular correctly
                    const typeMap: { [key: string]: string } = {
                        'emails': 'email',
                        'phones': 'phone',
                        'addresses': 'address',
                        'relatives': 'relative',
                        'names': 'name',
                        'akas': 'aka'
                    };
                    
                    const singularType = typeMap[type] || type.replace(/s$/, '');
                    
                    masked.push({
                        type: singularType,
                        value: displayValue,
                        companies: exposure.companies || [],
                        originalExposure: exposure
                    });
                }
            });
        });

        return masked;
    });

    // Enhanced function to get companies for addresses (including partial matches)
    const getCompaniesForAddress = (vaultAddress: any) => {
        if (!rawPotentialExposures.value?.addresses) return [];
        
        let allCompanies: any[] = [];
        
        // Check all exposure addresses for matches (both exact and partial)
        rawPotentialExposures.value.addresses.forEach((exposure: any) => {
            // Ensure exposure.value exists and has the expected structure
            if (!exposure.value || typeof exposure.value !== 'object') {
                return;
            }
            
            const isMatch = isPartialAddressMatch(exposure.value, {
                ...vaultAddress,
                region: vaultAddress.region || undefined
            });
            
            if (isMatch) {
                allCompanies = [...allCompanies, ...exposure.companies];
            }
        });
        
        // Remove duplicates based on company_id
        const uniqueCompanies = allCompanies.filter((company, index, self) =>
            index === self.findIndex(c => c.company_id === company.company_id)
        );
        
        return uniqueCompanies;
    };

    // Enhanced function to get companies for date of birth record
    const getCompaniesForDateOfBirth = (dob: string) => {
        if (!rawPotentialExposures.value) return [];
        
        let allCompanies: any[] = [];
        const personAge = calculateAge(dob);
        
        // Get companies from direct DOB matches
        rawPotentialExposures.value.date_of_birth?.forEach(exposure => {
            if (new Date(exposure.value).toDateString() === new Date(dob).toDateString()) {
                allCompanies = [...allCompanies, ...exposure.companies];
            }
        });
        
        // Get companies from age exposures that match calculated age
        if (personAge !== null) {
            rawPotentialExposures.value.age?.forEach(exposure => {
                if (parseInt(String(exposure.value)) === personAge) { // Fix: convert to string first
                    allCompanies = [...allCompanies, ...exposure.companies];
                }
            });
        }
        
        // Remove duplicates based on company_id
        const uniqueCompanies = allCompanies.filter((company, index, self) =>
            index === self.findIndex(c => c.company_id === company.company_id)
        );
        
        return uniqueCompanies;
    };

    // Enhanced function to get companies for email records
    const getCompaniesForEmail = (email: string) => {
        if (!rawPotentialExposures.value?.emails) return [];
        
        const emailExposure = rawPotentialExposures.value.emails.find(exp => exp.value === email);
        return emailExposure ? emailExposure.companies : [];
    };

    // Enhanced function to get companies for phone records (with smart matching)
    const getCompaniesForPhone = (vaultPhone: string) => {
        if (!rawPotentialExposures.value?.phones) return [];
        
        let allCompanies: any[] = [];
        
        // Check all exposure phones for matches (using normalized comparison)
        rawPotentialExposures.value.phones.forEach(exposure => {
            if (arePhoneNumbersEqual(exposure.value, vaultPhone)) {
                allCompanies = [...allCompanies, ...exposure.companies];
            }
        });
        
        // Remove duplicates based on company_id
        const uniqueCompanies = allCompanies.filter((company, index, self) =>
            index === self.findIndex(c => c.company_id === company.company_id)
        );
        
        return uniqueCompanies;
    };

    // Enhanced function to get companies for name records
    const getCompaniesForName = (name: string) => {
        if (!rawPotentialExposures.value?.name) return [];
        
        // Names are now strings, not objects
        const nameExposure = rawPotentialExposures.value.name.find(exp => exp.value === name);
        return nameExposure ? nameExposure.companies : [];
    };

    // Enhanced function to get companies for ignored/suppressed records (fixed timing)
    // Function to get companies for ignored records (used in templates)
    const getCompaniesForIgnoredRecord = (type: string, data: any) => {
        // Make sure we have potential exposures first
        if (!rawPotentialExposures.value) {
            return [];
        }
        
        let allCompanies: any[] = [];
        
        if (type === 'name' || type === 'aka') {
            const nameString = typeof data === 'string' ? data : 
                             (typeof data === 'object' ? 
                              `${data.first_name || ''} ${data.middle_name || ''} ${data.last_name || ''}`.trim() : 
                              '');
            
            const normalizedNameString = normalizeString(nameString);
            
            // Check name exposures
            rawPotentialExposures.value.name?.forEach(exposure => {
                if (normalizeString(exposure.value) === normalizedNameString) {
                    allCompanies = [...allCompanies, ...exposure.companies];
                }
            });
            
            // Check aka exposures
            rawPotentialExposures.value.aka?.forEach(exposure => {
                if (normalizeString(exposure.value) === normalizedNameString) {
                    allCompanies = [...allCompanies, ...exposure.companies];
                }
            });
        } else if (type === 'email') {
            rawPotentialExposures.value.emails?.forEach(exposure => {
                if (exposure.value === data) {
                    allCompanies = [...allCompanies, ...exposure.companies];
                }
            });
        } else if (type === 'phone') {
            rawPotentialExposures.value.phones?.forEach(exposure => {
                if (arePhoneNumbersEqual(exposure.value, data)) {
                    allCompanies = [...allCompanies, ...exposure.companies];
                }
            });
        } else if (type === 'address') {
            // Format the suppressed address the same way as exposures for exact matching
            let addressString = '';
            if (typeof data === 'string') {
                addressString = data;
            } else if (typeof data === 'object' && data !== null) {
                addressString = formatExposureAddress(data as any);
            }
            
            rawPotentialExposures.value.addresses?.forEach(exposure => {
                const exposureString = formatExposureAddress(exposure.value);
                if (exposureString === addressString) {
                    allCompanies = [...allCompanies, ...exposure.companies];
                }
            });
        } else if (type === 'age') {
            rawPotentialExposures.value.age?.forEach(exposure => {
                if (String(exposure.value) === String(data)) {
                    allCompanies = [...allCompanies, ...exposure.companies];
                }
            });
        } else if (type === 'date_of_birth' || type === 'dob') {
            rawPotentialExposures.value.date_of_birth?.forEach(exposure => {
                if (exposure.value === data) {
                    allCompanies = [...allCompanies, ...exposure.companies];
                }
            });
        } else if (type === 'relative') {
            rawPotentialExposures.value.relatives?.forEach(exposure => {
                // More intelligent matching for relatives
                const exposureRelative = exposure.value;
                const suppressedRelative = data;
                
                // Match by key properties: first_name, last_name, and age
                const firstNameMatch = exposureRelative.first_name === suppressedRelative.first_name;
                const lastNameMatch = exposureRelative.last_name === suppressedRelative.last_name;
                const ageMatch = String(exposureRelative.age) === String(suppressedRelative.age);
                
                if (firstNameMatch && lastNameMatch && ageMatch) {
                    allCompanies = [...allCompanies, ...exposure.companies];
                }
            });
        }
        
        // Remove duplicates based on company_id
        const uniqueCompanies = allCompanies.filter((company, index, self) =>
            index === self.findIndex(c => c.company_id === company.company_id)
        );
        
        return uniqueCompanies;
    };

    // Helper function to check if cache is valid
    const isCacheValid = (timestamp: number) => {
        return Date.now() - timestamp < CACHE_EXPIRY;
    };

    /**
     * Fetch potential exposures for the current user
     */
    async function fetchPotentialExposures(): Promise<void> {
        // Block retries if we recently had an error (prevents retry loops)
        if (hasRecentError.value) {
            return;
        }

        // If data is cached and valid, return immediately
        if (rawPotentialExposures.value && isCacheValid(potentialExposuresCacheTimestamp.value)) {
            return;
        }

        // If already loading, return the existing promise
        if (isLoadingPotentialExposures.value && pendingFetchPromise) {
            return pendingFetchPromise;
        }

        if (!personStore.person?.id) {
            showError("Error", 'No person found');
            return;
        }

        // Set loading state and create new promise
        isLoadingPotentialExposures.value = true;
        
        pendingFetchPromise = (async () => {
        
            try {
                rawPotentialExposures.value = await exposuresService.getPotentialExposures(personStore.person!.id);
                potentialExposuresCacheTimestamp.value = Date.now();
                
                // Clear error state on successful fetch
                hasRecentError.value = false;
                if (errorTimeout) {
                    clearTimeout(errorTimeout);
                    errorTimeout = null;
                }
            } catch (error: any) {
                // Set error state to prevent immediate retries
                hasRecentError.value = true;
                
                // Clear error state after 5 seconds to allow manual retries
                if (errorTimeout) clearTimeout(errorTimeout);
                errorTimeout = setTimeout(() => {
                    hasRecentError.value = false;
                }, 5000);
                
                showError("Error", error.message || 'Failed to fetch potential exposures');
            } finally {
                isLoadingPotentialExposures.value = false;
                pendingFetchPromise = null;
            }
        })();

        return pendingFetchPromise;
    }

    /**
     * Fetch suppressed records
     */
    async function fetchSuppressedRecords(): Promise<void> {
        try {
            suppressedRecords.value = await exposuresService.getSuppressedRecords();
        } catch (error: any) {
            showError("Error", error.message || 'Could not fetch suppressed records');
        }
    }

    // Other existing functions...
    async function updateExposure(isMyExposure: boolean, type: any, value: any): Promise<void> {
        try {
            await exposuresService.updateExposure(type, value, isMyExposure);
            // Refresh suppressed records to update the UI state
            await fetchSuppressedRecords();
            showSuccess("Success", 'Exposure updated');
        } catch (error: any) {
            showError("Error", error.message || 'Failed to update exposure');
        }
    }

    async function updatePotentialExposures(type: string, value: any, isMyExposure: boolean): Promise<void> {
        // This is now handled by the reactive computed property based on suppressions
        // No need to manually update the store
    }

    async function deleteSuppressedRecord(suppressionId: number): Promise<void> {
        try {
            await exposuresService.deleteSuppressedRecord(suppressionId);
            await fetchSuppressedRecords(); // Refresh suppressions
            showSuccess("Success", 'Suppression removed');
        } catch (error: any) {
            showError("Error", error.message || 'Failed to delete suppressed record');
        }
    }

    // Clear all vault data (useful for impersonation/person switching)
    const clearVaultData = () => {
        rawPotentialExposures.value = undefined;
        suppressedRecords.value = [];
        potentialExposuresCacheTimestamp.value = 0;
        isLoadingPotentialExposures.value = false;
        hasRecentError.value = false;
        pendingFetchPromise = null;
        
        // Clear error timeout if it exists
        if (errorTimeout) {
            clearTimeout(errorTimeout);
            errorTimeout = null;
        }
    };

    return {
        fetchPotentialExposures,
        fetchSuppressedRecords,
        potentialExposures, // This is now the computed filtered version
        partialExposures, // Export the new partial exposures
        hasExposures,
        hasUnsuppressedUnconfirmedExposures,
        potentialExposuresCacheTimestamp,
        isLoadingPotentialExposures, // Export loading state
        updateExposure,
        updatePotentialExposures,
        suppressedRecords,
        deleteSuppressedRecord,
        getCompaniesForAddress,
        getCompaniesForDateOfBirth, // Export the new function
        getCompaniesForEmail,
        getCompaniesForPhone, // Updated with smart matching
        getCompaniesForName,
        getCompaniesForIgnoredRecord, // Add the new function
        capitalizeNameForDisplay, // Export the new capitalize function
        clearVaultData, // Export the new clear function
    };
}, {
    persist: {
        pick: ['rawPotentialExposures', 'potentialExposuresCacheTimestamp'],
    },
});

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useVaultStore, import.meta.hot))
}
